import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { Note, NoteTagFull, NotesBootstrapPayload } from '@/types/note';

interface NotesStoreState {
  initialized: boolean;
  loading: boolean;
  bootstrapFailed: boolean;
  notes: Record<string, Note>;
  noteOrder: string[];
  tags: Record<string, NoteTagFull>;
  total: number;
  hasMore: boolean;
  /**
   * Count of notes loaded through the paginated list (bootstrap + loadMore).
   * Used as the next page offset. Distinct from noteOrder.length because
   * realtime events and search results also upsert notes into the store.
   */
  loadedCount: number;

  // Actions
  hydrate: (payload: NotesBootstrapPayload) => void;
  setLoading: (loading: boolean) => void;
  setBootstrapFailed: (failed: boolean) => void;
  upsertNote: (note: Note) => void;
  upsertNotes: (notes: Note[]) => void;
  removeNote: (noteId: string) => void;
  upsertTag: (tag: NoteTagFull) => void;
  removeTag: (tagId: string) => void;
  appendNotes: (notes: Note[], total: number, hasMore: boolean) => void;
  reset: () => void;
}

const sortNotesByLastEdited = (notes: Note[]) =>
  notes
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a.lastEditedAt).getTime();
      const bTime = new Date(b.lastEditedAt).getTime();
      return bTime - aTime; // Most recent first
    })
    .map((note) => note.id);

export const useNotesStore = create<NotesStoreState>()(
  conditionalDevtools(
    (set) => ({
      initialized: false,
      loading: false,
      bootstrapFailed: false,
      notes: {},
      noteOrder: [],
      tags: {},
      total: 0,
      hasMore: false,
      loadedCount: 0,

      hydrate: (payload) => {
        const notesById = payload.notes.reduce<Record<string, Note>>((acc, note) => {
          acc[note.id] = note;
          return acc;
        }, {});
        const tagsById = payload.tags.reduce<Record<string, NoteTagFull>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});

        set({
          initialized: true,
          loading: false,
          bootstrapFailed: false,
          notes: notesById,
          noteOrder: sortNotesByLastEdited(payload.notes),
          tags: tagsById,
          total: payload.total,
          hasMore: payload.hasMore,
          loadedCount: payload.notes.length,
        });
      },

      setLoading: (loading) => set({ loading }),
      setBootstrapFailed: (failed) => set({ bootstrapFailed: failed }),

      upsertNote: (note) =>
        set((state) => {
          const notes = { ...state.notes, [note.id]: note };
          return {
            notes,
            noteOrder: sortNotesByLastEdited(Object.values(notes)),
          };
        }),

      upsertNotes: (incoming) =>
        set((state) => {
          if (!incoming.length) {
            return state;
          }
          const notes = { ...state.notes };
          for (const note of incoming) {
            notes[note.id] = note;
          }
          return {
            notes,
            noteOrder: sortNotesByLastEdited(Object.values(notes)),
          };
        }),

      removeNote: (noteId) =>
        set((state) => {
          const notes = { ...state.notes };
          delete notes[noteId];
          return {
            notes,
            noteOrder: state.noteOrder.filter((id) => id !== noteId),
            total: Math.max(0, state.total - 1),
          };
        }),

      upsertTag: (tag) =>
        set((state) => ({
          tags: { ...state.tags, [tag.id]: tag },
        })),

      removeTag: (tagId) =>
        set((state) => {
          const tags = { ...state.tags };
          delete tags[tagId];
          // Also remove tag from all notes
          const notes = { ...state.notes };
          for (const noteId of Object.keys(notes)) {
            const note = notes[noteId];
            if (note.tags.some((t) => t.id === tagId)) {
              notes[noteId] = {
                ...note,
                tags: note.tags.filter((t) => t.id !== tagId),
              };
            }
          }
          return { tags, notes };
        }),

      appendNotes: (newNotes, total, hasMore) =>
        set((state) => {
          const notes = { ...state.notes };
          for (const note of newNotes) {
            notes[note.id] = note;
          }
          return {
            notes,
            noteOrder: sortNotesByLastEdited(Object.values(notes)),
            total,
            hasMore,
            loadedCount: state.loadedCount + newNotes.length,
          };
        }),

      reset: () =>
        set({
          initialized: false,
          loading: false,
          bootstrapFailed: false,
          notes: {},
          noteOrder: [],
          tags: {},
          total: 0,
          hasMore: false,
          loadedCount: 0,
        }),
    }),
    { name: 'NotesStore' }
  )
);

// Selectors
export const selectNoteById = (noteId: string) => (state: NotesStoreState) =>
  state.notes[noteId] ?? null;

export const selectAllNotes = (state: NotesStoreState) =>
  state.noteOrder.map((id) => state.notes[id]).filter(Boolean);

export const selectAllTags = (state: NotesStoreState) =>
  Object.values(state.tags).sort((a, b) => a.name.localeCompare(b.name));
